import { useCallback } from "react";
import { useForm } from "react-hook-form";
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

import type { HttpError } from "src-next/shared/api/httpClient";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import type { MessageTemplate } from "../types";
import type { MessageTemplateFormValues } from "../formTypes";

export interface UseMessageTemplateFormFlowOptions {
  initialValues?: MessageTemplateFormValues;
  onSubmitTemplate: (values: MessageTemplateFormValues) => Promise<void>;
}

export interface UseMessageTemplateFormFlowResult {
  control: Control<MessageTemplateFormValues>;
  formErrors: FieldErrors<MessageTemplateFormValues>;
  handleSubmit: ReturnType<UseFormHandleSubmit<MessageTemplateFormValues>>;
  isSubmitting: boolean;
  hasSubmitError: boolean;
  submitErrorMessage: string | null;
  resetForm: () => void;
  fillFromTemplate: (template: MessageTemplate) => void;
  triggerFieldValidation: (fieldName: keyof MessageTemplateFormValues) => void;
  setValue: UseFormSetValue<MessageTemplateFormValues>;
  trigger: UseFormTrigger<MessageTemplateFormValues>;
}

const DEFAULT_FORM_VALUES: MessageTemplateFormValues = {
  name: "",
  headerFormat: "TEXT",
  header: "",
  headerMediaFile: null,
  headerMediaHandle: "",
  headerMediaPreviewUrl: "",
  headerMediaFileName: "",
  body: "",
  hasButton: false,
  buttonType: "URL",
  buttonText: "",
  buttonValue: "",
};

function resolveFormHeaderFormat(
  template: MessageTemplate,
): MessageTemplateFormValues["headerFormat"] {
  if (
    template.headerFormat === "IMAGE" ||
    template.headerFormat === "VIDEO" ||
    template.headerFormat === "DOCUMENT"
  ) {
    return template.headerFormat;
  }
  if (template.headerFormat === "TEXT" || String(template.header ?? "").trim()) {
    return "TEXT";
  }
  return "NONE";
}

const DUPLICATE_NAME_ERROR_MESSAGE = "Já existe um template com esse nome para este idioma/conta";

function isNameConflictError(error: unknown): boolean {
  const httpError = error as HttpError | undefined;
  const status = httpError?.status;
  const message = String(httpError?.message ?? "").toLowerCase();
  const dataText = JSON.stringify(httpError?.data ?? "").toLowerCase();

  const hasNameConflictMessage =
    message.includes("já existe") ||
    message.includes("duplicate") ||
    message.includes("already exists") ||
    dataText.includes("já existe") ||
    dataText.includes("duplicate") ||
    dataText.includes("already exists");

  return status === 409 || hasNameConflictMessage;
}

function resolveSubmitErrorMessage(error: unknown): string | null {
  if (!error) return null;

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  const httpError = error as HttpError | undefined;
  const data = httpError?.data;
  if (data && typeof data === "object") {
    const envelope = data as Record<string, unknown>;
    const nestedError = envelope.error;
    if (nestedError && typeof nestedError === "object") {
      const nestedMessage = (nestedError as Record<string, unknown>).message;
      if (typeof nestedMessage === "string" && nestedMessage.trim()) {
        return nestedMessage.trim();
      }
    }
    if (typeof envelope.message === "string" && envelope.message.trim()) {
      return envelope.message.trim();
    }
  }

  if (typeof httpError?.message === "string" && httpError.message.trim()) {
    return httpError.message.trim();
  }

  return null;
}

export function useMessageTemplateFormFlow({
  initialValues,
  onSubmitTemplate,
}: UseMessageTemplateFormFlowOptions): UseMessageTemplateFormFlowResult {
  const defaultValues = initialValues ?? DEFAULT_FORM_VALUES;
  const {
    control,
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm<MessageTemplateFormValues>({
    mode: "onBlur",
    defaultValues,
  });

  const { run, loading: isSubmitting, error } = useAsyncTask<
    [MessageTemplateFormValues],
    void
  >({
    task: useCallback(
      async (values: MessageTemplateFormValues) => {
        await onSubmitTemplate(values);
      },
      [onSubmitTemplate],
    ),
  });

  const handleSubmit = reactHookFormHandleSubmit(async (values) => {
    clearErrors("name");

    try {
      await run(values);
    } catch (error) {
      if (isNameConflictError(error)) {
        setError("name", {
          type: "server",
          message: DUPLICATE_NAME_ERROR_MESSAGE,
        });
        return;
      }
      throw error;
    }
  });

  const resetForm = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
  }, [reset]);

  const fillFromTemplate = useCallback(
    (template: MessageTemplate) => {
      reset({
        name: template.name,
        headerFormat: resolveFormHeaderFormat(template),
        header: template.header ?? "",
        headerMediaFile: null,
        headerMediaHandle: template.headerMediaHandle ?? "",
        headerMediaPreviewUrl: template.headerMediaUrl ?? "",
        headerMediaFileName: template.headerMediaFileName ?? "",
        body: template.preview,
        hasButton: Boolean(template.buttonText),
        buttonType: template.buttonType as "URL",
        buttonText: template.buttonText ?? "",
        buttonValue: template.buttonValue ?? "",
      });
    },
    [reset],
  );

  const triggerFieldValidation = useCallback(
    (fieldName: keyof MessageTemplateFormValues) => {
      void trigger(fieldName);
    },
    [trigger],
  );

  return {
    control,
    formErrors: errors,
    handleSubmit,
    isSubmitting,
    hasSubmitError: Boolean(error) && !Boolean(errors.name),
    submitErrorMessage:
      Boolean(error) && !Boolean(errors.name)
        ? resolveSubmitErrorMessage(error) ??
          "Erro ao salvar template. Tente novamente."
        : null,
    resetForm,
    fillFromTemplate,
    triggerFieldValidation,
    setValue,
    trigger,
  };
}
